import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "./ui/button";
import {
  X, Play, Pause, RotateCcw, ChevronRight, ChevronLeft, Sparkles, Bot, FileText, Layers,
  Mic, Volume2, Menu, LogOut, Stethoscope, Wallet, BrainCircuit, MessagesSquare,
  ShieldCheck, ExternalLink, Globe, Edit3, HelpCircle, Gauge, ArrowRight,
  GraduationCap,
} from "lucide-react";

/**
 * CinematicWalkthrough v2 — full-product story for prospects/investors.
 *
 * Architecture (per Feb-2026 user request):
 *
 *   1. Title card           — "Built on an Intelligence Layer" cinematic intro
 *   2-7. Chat scenes        — Sara conversation, rendered inside the REAL chatbot
 *                             shell (dark-green header, left platform sidebar,
 *                             white chat canvas, disabled input). Read-only.
 *                             A persistent caption strip rotates the "Intelligence
 *                             Layer" differentiators per scene.
 *   8. Dashboard scenes     — Real screenshots of every operator section
 *                             (Overview → Embed) animated with a subtle
 *                             pan + caption overlay. Resources is split into
 *                             3 sub-scenes (Website / Editable docs / Q&A).
 *   9. Close                — Register CTA card
 *
 * Controls (top strip):
 *   · Pause / Resume   — pauses the typewriter / scene timer
 *   · Speed selector   — 0.5 / 0.75 (default) / 1 / 1.25 / 1.5
 *   · Restart          — back to title card
 *   · Exit             — close overlay
 *   · Progress strip   — clickable segments; jumps to that scene's start
 *
 * Top sticky banner (always visible during playback):
 *   "This demo is for Rudras-AI (Healthcare). Your assistant can be customized
 *    to sell your products, services, training etc., with your customizable
 *    resources including documents and image."
 *   With inline "Register at rudras-ai.com →" link (external).
 */

const REGISTER_URL = "https://rudras-ai.com/register";
const RUDRAS_LOGO = "/brand/rudras-logo.png";

const PLATFORM_USE_CASES = ["Sales", "Support", "Onboarding", "Training", "Knowledge", "Workflow"];

// ---- Intelligence Layer differentiators (rotate on each chat scene) ----
const INTEL_CAPTIONS = [
  { icon: BrainCircuit, label: "Intelligence Layer", text: "Underneath is a Senior-Sales-Person intelligence layer — not a generic chatbot." },
  { icon: MessagesSquare, label: "Discovery first",   text: "Sara asks before she pitches. Same instinct your best rep uses." },
  { icon: ShieldCheck,   label: "Value-led, not pushy", text: "Educates on value, never pressures. No discount-bait, no urgency tricks." },
  { icon: ExternalLink,  label: "Shareable link",     text: "A single link lands on your customer's screen — no waiting for them to visit your site." },
  { icon: Sparkles,      label: "Always on-brand",    text: "Trained on your documents, your tone, your pricing rules." },
  { icon: BrainCircuit,  label: "Quote with reasoning", text: "Explains pricing the way a human would — not a price-list lookup." },
];

// ---- Platform sidebar items (mirror the real public chatbot left panel) ----
const PLATFORM_MODULES = [
  { icon: Stethoscope,   title: "Cloud EMR",           text: "Specialty-tuned templates, e-prescribing, labs, and orders." },
  { icon: Wallet,        title: "Revenue Cycle",       text: "Real-time eligibility, denials, and clean-claim scrubbing." },
  { icon: BrainCircuit,  title: "Clinical AI",         text: "Ambient AI scribe — SOAP notes drafted in under a minute." },
  { icon: MessagesSquare, title: "Patient Engagement", text: "Portal, intake, scheduling, reminders, and telehealth." },
  { icon: ShieldCheck,   title: "Compliance & Trust",  text: "HIPAA · SOC 2 Type II · BAA included · audit logs." },
];
const COMMON_QUESTIONS = [
  "Can it explain our EMR and practice-management tools?",
  "How does it help after-hours patient inquiries?",
  "How does your RCM service improve our collections?",
  "How does this reduce front-desk workload?",
  "Can we get a pricing estimate for our practice?",
  "Can patients request appointments through the assistant?",
];

// ---- Chat scenes (6 conversation chapters) ----
const CHAT_SCENES = [
  { id: "consult",     idx: "02", title: "AI Product Consultation",          sub: "Sara opens with discovery, not a pitch" },
  { id: "specialty",   idx: "03", title: "Specialty Workflow Understanding", sub: "Tailored answers for Pain · OBGYN · Behavioral" },
  { id: "qualify",     idx: "04", title: "Lead Qualification",                sub: "Captures size, intent, urgency — automatically" },
  { id: "capture",     idx: "05", title: "Demo Request Capture",              sub: "Smart hand-off when the prospect is ready" },
  { id: "rag",         idx: "06", title: "Resource-Driven AI Responses",      sub: "Grounded in your tenant's knowledge base" },
  { id: "compliance",  idx: "07", title: "Compliance & Trust",                sub: "HIPAA · SOC 2 · BAA — answered with receipts" },
];
const CHAT_SCRIPTS = {
  consult: [
    { role: "assistant", text: "Hi! I'm Sara, the AI sales engineer for Rudras-AI Healthcare. What brings you in today?" },
    { role: "user",      text: "We're an 8-provider pain management group. Claim denials have climbed to 14%." },
    { role: "assistant", text: "That's a real pressure point. Two quick questions so I can be useful: are denials mostly clinical (missing documentation) or administrative (eligibility / coding)? And are you handling billing in-house?" },
    { role: "user",      text: "Mostly administrative — wrong payer IDs and eligibility. In-house with 2 coders." },
    { role: "assistant", text: "Got it. Two levers typically fix that: real-time eligibility verification 72h before each visit, and clean-claim scrubbing inside the EMR. Similar practices run a 97%+ acceptance rate and ~24-day average AR." },
  ],
  specialty: [
    { role: "user",      text: "Do you actually support pain management specifically?" },
    { role: "assistant", text: "Yes — PDMP integration, controlled-substance workflows, opioid risk assessments, and treatment-agreement signing all ship out of the box." },
    { role: "user",      text: "What about OBGYN — we're considering a sister practice." },
    { role: "assistant", text: "ACOG-aligned templates, prenatal flow sheets, EDD calculator, and ultrasound integrations are built in. Behavioral health, family practice, chiropractic, pediatrics, and allergy ship templates too." },
  ],
  qualify: [
    { role: "user",      text: "How much does the full suite cost?" },
    { role: "assistant", text: "Happy to share. To give a meaningful number — roughly how many providers, what specialty, and EMR-only or full RCM?" },
    { role: "user",      text: "8 pain providers, full suite including AI scribe." },
    { role: "assistant", text: "For an 8-provider pain practice on the full suite with AI scribe, you're on Premium — about $629/provider/month, ~$5,000/month total. RCM service is 4.5%–6.5% of collections. Implementation is waived above 5 providers." },
  ],
  capture: [
    { role: "user",      text: "Sounds promising. Can we set up a demo?" },
    { role: "assistant", text: "Absolutely. What's the best email to send two demo slots for this week?" },
    { role: "user",      text: "amelia.chen@example.com" },
    { role: "assistant", text: "Captured. Our team will reach out within the hour with two slots and a tailored pain-management walkthrough. Anything specific you want them to focus on first?" },
  ],
  rag: [
    { role: "user",      text: "How does your AI scribe handle controlled substances?" },
    { role: "assistant", text: "It transcribes locally on-device during the encounter, then drafts a structured SOAP note with the controlled-substance workflow already populated — PDMP query results and opioid-risk-assessment fields included. Clinician edits and signs in under a minute." },
    { role: "user",      text: "Where does the audio live?" },
    { role: "assistant", text: "Processed in-region, retained 30 days unless your practice opts to keep it longer. Never used to train shared models." },
  ],
  compliance: [
    { role: "user",      text: "Is everything compliant?" },
    { role: "assistant", text: "Yes — HIPAA-compliant with executed BAA, SOC 2 Type II audited annually, AES-256 at rest, TLS 1.3 in transit, and role-based audit logs retained for 6 years." },
    { role: "user",      text: "Any patient consent management?" },
    { role: "assistant", text: "Built in — granular consent capture for telehealth, recording, and outreach. Patients can revoke any time from the portal." },
  ],
};

// ---- Dashboard scenes (11 = 9 sections + Resources split into 3 sub-scenes) ----
// Each scene declares `captionPos` so the caption chip sits in the available
// whitespace for that screenshot — never on top of the primary content.
const DASH_SCENES = [
  { id: "ov",     idx: "08", img: "/cinematic/overview.jpg",      title: "Overview",            caption: "At-a-glance metrics — leads captured, conversations, AI engagement, and assistant health.", focus: { x: 50, y: 50, zoom: 1.0, panY: true }, captionPos: "bl" },
  { id: "bp",     idx: "09", img: "/cinematic/profile.jpg",       title: "Business Profile",    caption: "Single source of truth — company, industry, offerings, theme color. Powers every reply.", focus: { x: 50, y: 30, zoom: 1.10 }, captionPos: "br" },
  { id: "res1",   idx: "10a", img: "/cinematic/resources.jpg",    title: "Resources · Website Extraction", caption: "Paste a URL — Rudras-AI crawls and indexes it in seconds. No copy-paste.", focus: { x: 30, y: 30, zoom: 1.18 }, captionPos: "br" },
  { id: "res2",   idx: "10b", img: "/cinematic/resources.jpg",    title: "Resources · Editable Documents", caption: "Edit, replace, or remove any document. Changes re-index automatically.",       focus: { x: 50, y: 55, zoom: 1.18 }, captionPos: "br" },
  { id: "res3",   idx: "10c", img: "/cinematic/resources-qa.jpg", title: "Resources · Q&A Overrides",      caption: "Curate exact answers for your top questions. The assistant uses them verbatim.",  focus: { x: 50, y: 50, zoom: 1.05 }, captionPos: "tr" },
  { id: "brand",  idx: "11", img: "/cinematic/branding.jpg",      title: "Branding & Persona", caption: "Tune the assistant's voice, persona, suggested prompts, and brand color in minutes.", focus: { x: 50, y: 40, zoom: 1.08 }, captionPos: "br" },
  { id: "leads",  idx: "12", img: "/cinematic/leads.jpg",         title: "Leads",               caption: "Every captured prospect — scored High / Medium / Marketing — with full transcript context.", focus: { x: 50, y: 45, zoom: 1.05 }, captionPos: "br" },
  { id: "convo",  idx: "13", img: "/cinematic/conversations.jpg", title: "Conversations",       caption: "Every chat session, fully searchable. Replay the moment a lead converted.",         focus: { x: 50, y: 45, zoom: 1.05 }, captionPos: "bl" },
  { id: "email",  idx: "14", img: "/cinematic/email-logs.jpg",    title: "Email Logs",          caption: "Transparent audit trail of every transcript, demo request, and lead notification.",     focus: { x: 50, y: 45, zoom: 1.05 }, captionPos: "br" },
  { id: "share",  idx: "15", img: "/cinematic/share.jpg",         title: "Share Link / QR",     caption: "One link — drop into emails, QR codes for conferences, or paste into any campaign.", focus: { x: 50, y: 35, zoom: 1.04 }, captionPos: "bl" },
  { id: "embed",  idx: "16", img: "/cinematic/embed.jpg",         title: "Embed",               caption: "A 2-line snippet adds your assistant to any website, landing page, or product.",     focus: { x: 50, y: 45, zoom: 1.10 }, captionPos: "br" },
];

// Resources sub-scenes get ~10sec each. Overview scrolls top→bottom over
// ~14sec so the viewer can read the entire full-page screenshot. Other
// dashboard scenes ~9sec each.
const DASH_DWELL_MS = (id) => {
  if (id === "ov") return 14000;
  if (id.startsWith("res")) return 10000;
  return 9000;
};

const PACE = {
  // Letter-by-letter typing was retired Feb-2026 per user request — viewers
  // didn't have the patience for it. Scene 02 still reveals turns one-at-a-
  // time (so viewers see the Q→A rhythm), but each bubble appears instantly.
  // Scenes 03–07 pre-populate the entire transcript at scene-start.
  turnGapMs: 1200,           // gap between turns in scene 02 (one-by-one mode)
  sceneReadMs: 9000,         // dwell time on a fully-populated chat scene
  sectionGapMs: 1100,
  titleHoldMs: 10000,        // first title slide — hold for 10 seconds (user req)
};

// =====================================================================
//                                CHROME
// =====================================================================

function Bubble({ role, text, partial }) {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} my-2 animate-[fadeIn_0.3s_ease-out]`}>
      <div
        className={`max-w-[78%] px-4 py-3 rounded-2xl text-[15px] leading-relaxed shadow-sm ${
          isUser ? "bg-[#0E5C4E] text-white rounded-br-md" : "bg-white text-slate-800 rounded-bl-md border border-slate-200"
        }`}
      >
        {partial ?? text}
        {partial != null && partial.length < text.length && (
          <span className="inline-block w-[2px] h-[1em] align-middle ml-0.5 bg-current animate-pulse" />
        )}
      </div>
    </div>
  );
}

function TypingDots() {
  return (
    <div className="flex justify-start my-2">
      <div className="px-4 py-3 rounded-2xl bg-white shadow-sm rounded-bl-md border border-slate-200">
        <span className="inline-flex gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "0ms" }} />
          <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "150ms" }} />
          <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "300ms" }} />
        </span>
      </div>
    </div>
  );
}

/** Read-only replica of the real Public Chatbot shell.
 *  No clicks register — purely cinematic chrome around the conversation stage. */
function ChatbotShellMock({ scrollerRef, turns, partial, showTyping }) {
  return (
    <div className="w-full max-w-[1280px] mx-auto rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-[#F7F8FC] text-slate-800" style={{ animation: "fadeIn 0.4s ease-out" }}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-[#0E5C4E] text-white">
        <div className="flex items-center gap-2.5">
          <button className="w-8 h-8 rounded-md hover:bg-white/10 grid place-items-center cursor-default" tabIndex={-1} aria-hidden>
            <Menu className="w-4 h-4" />
          </button>
          <div className="w-9 h-9 rounded-lg bg-[#0b1a3b] grid place-items-center overflow-hidden ring-1 ring-white/15">
            <img src={RUDRAS_LOGO} alt="" className="w-full h-full object-contain" />
          </div>
          <div>
            <div className="font-semibold text-sm leading-tight">Rudras-AI Healthcare Solutions</div>
            <div className="text-[11px] text-white/80 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 inline-block" />
              Online · Healthcare AI Sales Engineer
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-white/20 text-white/85">
            <Mic className="w-3 h-3" /> Voice chat
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/15 text-white">
            <Layers className="w-3 h-3" /> Brief
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-white/20 text-white/80">
            <FileText className="w-3 h-3" /> Elaborate
          </span>
          <span className="w-7 h-7 rounded-md grid place-items-center border border-white/20 text-white/85">
            <Volume2 className="w-3.5 h-3.5" />
          </span>
          <span className="px-3 py-1.5 rounded-md bg-amber-300 text-emerald-950 font-medium">Get in touch</span>
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-white/20 text-white/80">
            <LogOut className="w-3 h-3" /> Exit
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="flex" style={{ height: "min(540px, 56vh)" }}>
        {/* Sidebar */}
        <aside className="w-[300px] border-r border-slate-200 bg-gradient-to-b from-white to-slate-50/60 overflow-y-auto">
          <div className="px-4 pt-5 pb-3">
            <div className="text-[10.5px] font-bold tracking-[0.14em] uppercase text-slate-500 mb-2.5">Our Platform</div>
            <div className="space-y-2">
              {PLATFORM_MODULES.map((m) => {
                const Icon = m.icon;
                return (
                  <div key={m.title} className="flex items-start gap-3 p-2.5 rounded-lg border border-slate-200 bg-white">
                    <div className="w-8 h-8 rounded-md grid place-items-center bg-[#0E5C4E]/10 text-[#0E5C4E] flex-shrink-0">
                      <Icon className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="text-[12.5px] font-semibold text-slate-900 leading-tight">{m.title}</div>
                      <div className="text-[11.5px] text-slate-500 mt-0.5 leading-snug">{m.text}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="px-4 pt-2 pb-5">
            <div className="text-[10.5px] font-bold tracking-[0.14em] uppercase text-slate-500 mb-2.5">Common Questions</div>
            <div className="space-y-0.5">
              {COMMON_QUESTIONS.map((q) => (
                <div key={q} className="text-[12.5px] px-2.5 py-2 rounded-md text-slate-700">
                  {q}
                </div>
              ))}
            </div>
          </div>
        </aside>
        {/* Chat canvas */}
        <div className="flex-1 flex flex-col min-w-0">
          <div ref={scrollerRef} className="flex-1 overflow-y-auto px-6 pt-5 pb-3">
            <div className="max-w-[680px] mx-auto">
              {turns.map((t, i) => <Bubble key={i} role={t.role} text={t.text} />)}
              {partial && <Bubble role={partial.role} text={partial.text} partial={partial.partial} />}
              {showTyping && <TypingDots />}
            </div>
          </div>
          <div className="border-t border-slate-200 bg-white px-6 py-3">
            <div className="max-w-[680px] mx-auto flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-2xl px-3 py-2 opacity-60">
              <input disabled placeholder="Ask Sara anything…" className="flex-1 bg-transparent outline-none text-sm placeholder:text-slate-400 cursor-not-allowed" />
              <span className="w-9 h-9 rounded-xl bg-[#0E5C4E]/50 grid place-items-center">
                <ArrowRight className="w-4 h-4 text-white" />
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const CAPTION_POSITIONS = {
  tl: "top-4 left-4",
  tr: "top-4 right-4",
  bl: "bottom-4 left-4",
  br: "bottom-4 right-4",
};

/** Dashboard scene — pannned + zoomed screenshot with caption banner.
 *  When `focus.panY === true` the image is rendered tall (object-contain,
 *  width-locked) and animated with a slow top→bottom translate so the viewer
 *  can read the entire long screenshot (used by Overview / Resources tall
 *  pages where a fixed-zoom crop would hide content). */
function DashboardSceneCard({ scene, chapterLabel }) {
  const { img, title, caption, focus, captionPos } = scene;
  const panY = focus?.panY === true;
  const focusX = focus?.x ?? 50;
  const focusY = focus?.y ?? 45;
  const zoom = focus?.zoom ?? 1.06;
  const posClass = CAPTION_POSITIONS[captionPos] || CAPTION_POSITIONS.bl;
  return (
    <div className="w-full max-w-[1280px] mx-auto" style={{ animation: "fadeIn 0.45s ease-out" }}>
      <div className="rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-slate-900 relative">
        <div className="relative" style={{ height: "min(560px, 60vh)", overflow: "hidden" }}>
          {panY ? (
            <img
              src={img}
              alt=""
              className="absolute left-0 right-0 mx-auto w-full block animate-[rch-pan-y_14s_ease-in-out_forwards]"
              style={{ top: 0 }}
            />
          ) : (
            <img
              src={img}
              alt=""
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-[5000ms] ease-out"
              style={{
                transformOrigin: `${focusX}% ${focusY}%`,
                transform: `scale(${zoom})`,
              }}
            />
          )}
          <div className={`absolute ${posClass} w-[280px] sm:w-[300px] bg-emerald-950/95 backdrop-blur-sm border border-amber-300/40 rounded-xl px-3.5 py-2.5 shadow-2xl flex flex-col gap-1.5 z-10`}>
            <div className="inline-flex items-center self-start gap-0">
              <span className="px-2 py-0.5 rounded-md bg-amber-300 text-emerald-950 text-[10.5px] font-bold uppercase tracking-[0.14em]">
                {chapterLabel}
              </span>
            </div>
            <div className="font-display text-amber-200 text-[16px] sm:text-[17px] font-semibold leading-snug">{title}</div>
            <div className="text-amber-100/95 text-[12.5px] sm:text-[13px] font-medium leading-snug">{caption}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// =====================================================================
//                          MAIN COMPONENT
// =====================================================================

/**
 * Build full timeline of scenes in playback order. Each entry has:
 *   { id, type: "title"|"chat"|"dash"|"register", idx, title, sub?, intel? }
 */
function buildTimeline() {
  const tl = [];
  tl.push({ id: "title", type: "title", idx: "00", title: "Built on an Intelligence Layer" });
  CHAT_SCENES.forEach((s, i) => tl.push({ ...s, type: "chat", intel: INTEL_CAPTIONS[i] }));
  DASH_SCENES.forEach((s) => tl.push({ ...s, type: "dash" }));
  tl.push({ id: "register", type: "register", idx: "17", title: "Your turn." });
  return tl;
}

export default function CinematicWalkthrough({ open, onClose, autoStart = false, marketingCta = false, onMarketingCta, onSkipToInteractive, embed = false }) {
  const TIMELINE = useMemo(buildTimeline, []);
  const [sceneIdx, setSceneIdx] = useState(0);
  const [turns, setTurns] = useState([]);
  const [partial, setPartial] = useState(null);
  const [showTyping, setShowTyping] = useState(false);
  const [phase, setPhase] = useState("title"); // "title" | "play" | "done"
  const [playing, setPlaying] = useState(false);
  const [paused, setPaused] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(0.75);
  const speedRef = useRef(0.75);
  const pauseRef = useRef(false);
  useEffect(() => { speedRef.current = playbackSpeed; }, [playbackSpeed]);
  useEffect(() => { pauseRef.current = paused; }, [paused]);
  const cancelRef = useRef({ cancelled: false, jumpTo: null, timers: new Set() });
  const scrollerRef = useRef(null);
  const scene = TIMELINE[sceneIdx];

  // ---- lifecycle ----
  useEffect(() => {
    if (!open) return;
    cancelRef.current.cancelled = false;
    setSceneIdx(0);
    setTurns([]); setPartial(null); setShowTyping(false);
    setPhase("title"); setPlaying(false); setPaused(false);
    if (autoStart) {
      const t = setTimeout(() => { play(); }, 1400);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, autoStart]);

  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [turns, partial, showTyping]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // ---- pacing utilities ----
  // sleep with pause-aware polling. Cancellation aborts immediately.
  // Chat scenes (02–07) play at a baked-in 3× speed regardless of the slider
  // — per Feb-2026 user request, the typewriter felt too deliberate during
  // the conversation segment. Title / dashboard / register scenes still
  // respect the slider so viewers can take their time reading.
  function effectiveSpeed() {
    const cur = TIMELINE[sceneIdx];
    if (cur?.type === "chat") return 3;
    return speedRef.current || 1;
  }
  async function sleep(ms) {
    const scaled = Math.max(10, ms / effectiveSpeed());
    const end = performance.now() + scaled;
    while (performance.now() < end) {
      if (cancelRef.current.cancelled) return;
      // While paused, freeze the deadline by extending it
      if (pauseRef.current) {
        const pauseStart = performance.now();
        // Spin in small slices so pause/cancel both react quickly
        while (pauseRef.current && !cancelRef.current.cancelled) {
          await new Promise((r) => setTimeout(r, 80));
        }
        const pausedFor = performance.now() - pauseStart;
        // Slide the deadline forward by the paused duration
        // (re-compute relative to a fresh `now` to avoid drift)
        // We do this by mutating the loop's local `end` variable.
        // eslint-disable-next-line no-param-reassign
        ms = Math.max(0, end - performance.now() + pausedFor); // unused, retained for clarity
      }
      await new Promise((r) => {
        const t = setTimeout(r, 60);
        cancelRef.current.timers.add(t);
      });
    }
  }

  async function playChatTurnByTurn(script) {
    // First chat scene (Scene 02 — "I am Sara…") — reveal turns one-by-one
    // INSTANTLY so the viewer sees the Q→A rhythm without typewriter delay.
    setTurns([]); setPartial(null); setShowTyping(false);
    for (let i = 0; i < script.length; i++) {
      if (cancelRef.current.cancelled) return;
      while (pauseRef.current && !cancelRef.current.cancelled) await new Promise(r => setTimeout(r, 80));
      setTurns(prev => [...prev, script[i]]);
      await sleep(PACE.turnGapMs);
    }
  }

  function playChatAllAtOnce(script) {
    // Scenes 03–07 — render the entire transcript at once so the viewer can
    // read at their own pace (pause if they want more time).
    setShowTyping(false); setPartial(null);
    setTurns(script.map(t => ({ role: t.role, text: t.text })));
  }

  async function playScene(idx) {
    setSceneIdx(idx);
    setTurns([]); setPartial(null); setShowTyping(false);
    const s = TIMELINE[idx];
    if (!s) return;
    if (s.type === "title" || s.type === "register") {
      setPhase("play");
      await sleep(s.type === "title" ? PACE.titleHoldMs : 6000);
      return;
    }
    if (s.type === "chat") {
      setPhase("play");
      const script = CHAT_SCRIPTS[s.id] || [];
      // Scene 02 ("consult") reveals turns one-by-one; subsequent chat scenes
      // pre-populate the entire transcript so the viewer can read on their
      // own schedule (pause + scrub).
      if (s.id === "consult") {
        await playChatTurnByTurn(script);
      } else {
        playChatAllAtOnce(script);
        await sleep(PACE.sceneReadMs);
      }
      await sleep(PACE.sectionGapMs);
      return;
    }
    if (s.type === "dash") {
      setPhase("play");
      await sleep(DASH_DWELL_MS(s.id));
      return;
    }
  }

  async function play() {
    if (playing) return;
    setPlaying(true);
    cancelRef.current.cancelled = false;
    let i = 0;
    while (i < TIMELINE.length) {
      if (cancelRef.current.cancelled) break;
      await playScene(i);
      // Honor scrubber-jump
      if (cancelRef.current.jumpTo != null) {
        i = cancelRef.current.jumpTo;
        cancelRef.current.jumpTo = null;
        cancelRef.current.cancelled = false;
        continue;
      }
      i += 1;
    }
    if (!cancelRef.current.cancelled) setPhase("done");
    setPlaying(false);
  }

  function clearTimers() {
    cancelRef.current.timers.forEach(clearTimeout);
    cancelRef.current.timers.clear();
  }

  function restart() {
    // PERMANENT FIX (Feb-2026 user feedback): "Restart" must always land the
    // viewer on the Cover screen, NOT auto-replay Scene 01. The Cover screen
    // is the platform pitch the user spent the most effort on — visitors who
    // hit Restart want to re-see that, then choose when to Play again.
    cancelRef.current.cancelled = true;
    clearTimers();
    setPaused(false);
    setTimeout(() => {
      setSceneIdx(0);
      setTurns([]); setPartial(null); setShowTyping(false);
      setPhase("title");
      setPlaying(false);          // <- key bit: stop the play loop entirely
      cancelRef.current.cancelled = false;
      cancelRef.current.jumpTo = null;
    }, 120);
  }

  function jumpTo(targetIdx) {
    // Hard-jump: cancel current scene loop, mark next target, restart play loop
    cancelRef.current.jumpTo = targetIdx;
    cancelRef.current.cancelled = true;
    clearTimers();
    setPaused(false);
    setTurns([]); setPartial(null); setShowTyping(false);
    setTimeout(() => {
      cancelRef.current.cancelled = false;
      setSceneIdx(targetIdx);
      if (!playing) play();
      else {
        // play() loop ended due to cancellation; restart it from targetIdx
        cancelRef.current.jumpTo = null;
        (async () => {
          setPlaying(true);
          let i = targetIdx;
          while (i < TIMELINE.length) {
            if (cancelRef.current.cancelled) break;
            await playScene(i);
            if (cancelRef.current.jumpTo != null) { i = cancelRef.current.jumpTo; cancelRef.current.jumpTo = null; cancelRef.current.cancelled = false; continue; }
            i += 1;
          }
          if (!cancelRef.current.cancelled) setPhase("done");
          setPlaying(false);
        })();
      }
    }, 120);
  }

  function exit() {
    cancelRef.current.cancelled = true;
    clearTimers();
    onClose?.();
  }

  if (!open) return null;

  // -------------------- render --------------------
  const showCover = !playing && phase === "title" && sceneIdx === 0 && turns.length === 0;
  const IntelIcon = scene?.intel?.icon || BrainCircuit;

  return (
    <div
      className="fixed inset-0 z-[1000] flex flex-col bg-gradient-to-br from-[#0b3a31] via-[#0e5c4e] to-[#06241e] text-white"
      data-testid="cinematic-walkthrough"
      style={{ animation: "fadeIn 0.4s ease-out" }}
    >
      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes scaleIn { from { opacity: 0; transform: scale(0.96) } to { opacity: 1; transform: scale(1) } }
        /* Vertical pan — used for tall full-page screenshots (Overview).
           Starts at the top of the image, slowly translates up so the bottom
           rises into view, then settles. translateY(0) → translateY(-(imageH-viewportH)).
           We use a calc fallback that works for ~2400-3200 px tall captures. */
        @keyframes rch-pan-y {
          0%   { transform: translateY(0); }
          15%  { transform: translateY(0); }
          85%  { transform: translateY(calc(-100% + min(560px, 60vh))); }
          100% { transform: translateY(calc(-100% + min(560px, 60vh))); }
        }
      `}</style>

      {/* ============ STICKY TOP BANNER (always visible during playback) ============ */}
      {!showCover && phase !== "done" && (
        <div
          className="px-4 sm:px-6 py-3 bg-amber-300 text-emerald-950 flex items-center justify-center gap-3 sm:gap-4 flex-wrap"
          data-testid="walkthrough-context-banner"
        >
          <Sparkles className="w-5 h-5 shrink-0" />
          <span className="text-center leading-snug">
            <span className="font-bold text-[15px] sm:text-[16px]">This demo is for Rudras-AI.</span>{" "}
            <span className="font-bold text-[17px] sm:text-[19px]">Customize AI Assistants for Your Business — Products, Services, Training &amp; More.</span>
          </span>
          <a
            href={REGISTER_URL}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-900 text-amber-200 font-semibold text-[13px] sm:text-[14px] hover:bg-emerald-800 transition"
            data-testid="walkthrough-register-cta"
          >
            Register at rudras-ai.com <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      )}

      {/* ============ TOP CONTROL BAR ============ */}
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 px-4 sm:px-6 py-3 border-b border-white/10 backdrop-blur-sm">
        {/* LEFT — brand block (logo + Rudras-ai + presenter info) */}
        <div className="flex items-center gap-3.5 min-w-0">
          <div className="w-[58px] h-[58px] sm:w-[64px] sm:h-[64px] rounded-xl overflow-hidden bg-[#0b1a3b] grid place-items-center shrink-0 shadow-xl ring-2 ring-amber-300/30">
            <img src={RUDRAS_LOGO} alt="Rudras-AI logo" className="w-full h-full object-contain" data-testid="walkthrough-brand-logo" />
          </div>
          <div className="min-w-0 leading-tight">
            <div className="font-display text-[19px] sm:text-[22px] font-bold text-white tracking-tight">
              <span style={{ fontStyle: "italic", fontWeight: 800 }}>R</span>udras-AI
            </div>
            <div className="text-[14px] sm:text-[16px] font-extrabold leading-tight mt-1 tracking-tight text-amber-300" data-testid="walkthrough-presenter">
              Sridhar Babu · 408-823-1402
            </div>
          </div>
        </div>

        {/* CENTER — platform tagline + progress */}
        <div className="hidden md:flex flex-col items-center text-center px-4 min-w-0">
          <div className="font-display text-[15px] lg:text-[17px] font-bold text-white uppercase tracking-[0.18em] whitespace-nowrap">
            Assistant Platform for Modern Businesses
          </div>
          <div className="text-white/55 text-[11px] tracking-wider uppercase truncate mt-0.5" data-testid="walkthrough-progress-label">
            {scene ? `${String(sceneIdx + 1).padStart(2, "0")} / ${String(TIMELINE.length).padStart(2, "0")} · ${scene.title}` : ""}
          </div>
        </div>

        {/* RIGHT — playback controls */}
        <div className="flex items-center justify-end gap-1.5 sm:gap-2 shrink-0 min-w-0">
          {/* Speed selector */}
          <div className="hidden sm:inline-flex items-center gap-1 px-1 py-1 bg-white/[0.06] border border-white/15 rounded-full" data-testid="walkthrough-speed-group" role="radiogroup" aria-label="Playback speed">
            <Gauge className="w-3 h-3 text-white/50 ml-1.5" aria-hidden />
            {[0.5, 0.75, 1, 1.25, 1.5].map((s) => (
              <button
                key={s}
                type="button"
                role="radio"
                aria-checked={playbackSpeed === s}
                onClick={() => setPlaybackSpeed(s)}
                className={`text-[11px] font-medium px-2 py-0.5 rounded-full transition ${
                  playbackSpeed === s ? "bg-emerald-400 text-emerald-950" : "text-white/70 hover:text-white"
                }`}
                data-testid={`walkthrough-speed-${String(s).replace(".", "_")}`}
              >
                {s}x
              </button>
            ))}
          </div>
          {/* Pause/Resume */}
          {playing && (
            <Button
              onClick={() => setPaused(p => !p)}
              size="sm"
              className="bg-white text-emerald-900 hover:bg-white/90"
              data-testid="walkthrough-pause-resume"
            >
              {paused ? <><Play className="w-3.5 h-3.5 mr-1.5" /> Resume</> : <><Pause className="w-3.5 h-3.5 mr-1.5" /> Pause</>}
            </Button>
          )}
          {/* Play */}
          {!playing && phase !== "done" && (
            <Button onClick={play} size="sm" className="bg-white text-emerald-900 hover:bg-white/90" data-testid="walkthrough-play">
              <Play className="w-3.5 h-3.5 mr-1.5" /> Play
            </Button>
          )}
          {onSkipToInteractive && (
            <Button onClick={() => { cancelRef.current.cancelled = true; onSkipToInteractive(); }} size="sm" variant="outline" className="hidden lg:inline-flex border-emerald-300/50 text-emerald-100 hover:bg-emerald-400/15 hover:text-white" data-testid="walkthrough-skip-interactive">
              Skip to interactive demo <ChevronRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          )}
          <Button onClick={restart} size="sm" variant="outline" className="border-white/30 text-white hover:bg-white/10 hover:text-white" data-testid="walkthrough-restart">
            <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> <span className="hidden sm:inline">Restart</span>
          </Button>
          {!embed && (
            <Button onClick={exit} size="sm" variant="ghost" className="text-white/80 hover:bg-white/10 hover:text-white" data-testid="walkthrough-exit">
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* ============ CLICKABLE PROGRESS STRIP ============ */}
      <div className="px-4 sm:px-6 pt-3 pb-1">
        <div className="grid grid-flow-col auto-cols-fr gap-1" role="tablist" aria-label="Scene navigation">
          {TIMELINE.map((s, i) => {
            const completed = i < sceneIdx;
            const active = i === sceneIdx;
            const label = `${String(i + 1).padStart(2, "0")} · ${s.title}`;
            return (
              <button
                key={s.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => jumpTo(i)}
                title={label}
                data-testid={`walkthrough-jump-${s.id}`}
                className={`group relative h-1.5 rounded-full overflow-hidden transition-all duration-300 cursor-pointer ${
                  active ? "bg-white/20" : completed ? "bg-emerald-400/80" : "bg-white/10 hover:bg-white/20"
                }`}
              >
                <span
                  className={`absolute inset-y-0 left-0 ${active ? "bg-emerald-400 w-1/2 animate-pulse" : "w-0"}`}
                />
                {/* Tooltip on hover */}
                <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 -top-7 px-1.5 py-0.5 text-[10px] bg-black/80 text-white rounded opacity-0 group-hover:opacity-100 transition whitespace-nowrap z-10">
                  {label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ============ STAGE ============ */}
      <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-6 sm:py-8 relative">
        {/* Floating prev/next navigation — large, obvious, always visible
            once playback has started so viewers don't rely on the thin
            progress strip up top. */}
        {!showCover && (
          <>
            <button
              type="button"
              onClick={() => jumpTo(Math.max(0, sceneIdx - 1))}
              disabled={sceneIdx === 0}
              data-testid="walkthrough-nav-prev"
              aria-label="Previous scene"
              className="hidden md:flex fixed left-3 lg:left-6 top-1/2 -translate-y-1/2 z-[1010] w-12 h-12 lg:w-14 lg:h-14 rounded-full bg-emerald-950/85 hover:bg-emerald-900 border border-amber-300/40 text-amber-200 items-center justify-center shadow-2xl backdrop-blur transition disabled:opacity-25 disabled:cursor-not-allowed hover:scale-105"
            >
              <ChevronLeft className="w-6 h-6 lg:w-7 lg:h-7" />
            </button>
            <button
              type="button"
              onClick={() => jumpTo(Math.min(TIMELINE.length - 1, sceneIdx + 1))}
              disabled={sceneIdx >= TIMELINE.length - 1}
              data-testid="walkthrough-nav-next"
              aria-label="Next scene"
              className="hidden md:flex fixed right-3 lg:right-6 top-1/2 -translate-y-1/2 z-[1010] w-12 h-12 lg:w-14 lg:h-14 rounded-full bg-amber-300 hover:bg-amber-200 text-emerald-950 items-center justify-center shadow-2xl border border-amber-200/60 transition disabled:opacity-25 disabled:cursor-not-allowed hover:scale-105"
            >
              <ChevronRight className="w-6 h-6 lg:w-7 lg:h-7" />
            </button>
          </>
        )}

        {/* Cover */}
        {showCover && (
          <div className="text-center max-w-4xl mx-auto px-2 py-2" style={{ animation: "scaleIn 0.5s ease-out" }} data-testid="walkthrough-cover">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-white/10 rounded-full text-sm sm:text-[15px] uppercase tracking-[0.3em] text-white/85 mb-7">
              <BrainCircuit className="w-4 h-4" /> Built on an Intelligence Layer
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.08] mb-8">
              Your <span className="text-emerald-300">AI Employee</span> — Built on the Intelligence Layer for Modern Businesses.
            </h1>
            <div className="max-w-3xl mx-auto text-left space-y-4 mb-9">
              {[
                { icon: Bot,            text: "An AI Assistant that learns your business, engages your prospects, captures leads, and scales customer interactions intelligently." },
                { icon: GraduationCap,  text: "Train your internal teams with AI-powered learning and quizzes." },
                { icon: MessagesSquare, text: "Introduce new products and services through interactive customer education and guided conversations." },
              ].map((row, i) => {
                const I = row.icon;
                return (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-emerald-400/15 border border-emerald-300/40 grid place-items-center shrink-0 mt-0.5">
                      <I className="w-5 h-5 sm:w-5.5 sm:h-5.5 text-emerald-300" />
                    </div>
                    <div className="text-white/95 text-lg sm:text-xl leading-relaxed">{row.text}</div>
                  </div>
                );
              })}
            </div>
            <div className="font-display text-2xl sm:text-3xl font-semibold text-white mb-3">
              One Assistant. <span className="text-emerald-300">Multiple Roles.</span>
            </div>
            <div className="flex flex-wrap justify-center gap-2 mb-5">
              {PLATFORM_USE_CASES.map((u) => (
                <span key={u} className="px-3 py-1.5 text-xs sm:text-[13px] uppercase tracking-wider bg-white/[0.08] border border-white/20 rounded-full text-white/85 font-medium">
                  {u}
                </span>
              ))}
            </div>
            <div className="italic text-white/75 text-base sm:text-lg mb-9">
              Powered by Your Resources. Guided by Your Instructions.
            </div>
            <Button size="lg" onClick={play} className="bg-emerald-400 text-emerald-950 hover:bg-emerald-300 text-base sm:text-lg px-9 py-6 font-semibold shadow-[0_10px_40px_-10px_rgba(52,211,153,0.6)]" data-testid="walkthrough-start">
              <Play className="w-5 h-5 mr-2" /> Start the walkthrough
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}

        {/* Title scene (intro) */}
        {!showCover && scene?.type === "title" && phase === "play" && (
          <div className="text-center max-w-5xl mx-auto" style={{ animation: "scaleIn 0.6s ease-out" }} data-testid="title-scene">
            <div className="inline-flex items-center gap-2 text-emerald-300 text-sm sm:text-base uppercase tracking-[0.3em] mb-6">
              <BrainCircuit className="w-5 h-5" /> Intelligence Layer
            </div>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl font-semibold leading-[1.05] mb-10">{scene.title}</h2>
            <div className="grid sm:grid-cols-3 gap-5 sm:gap-6 max-w-4xl mx-auto text-left">
              {INTEL_CAPTIONS.slice(0, 3).map((c) => {
                const I = c.icon;
                return (
                  <div key={c.label} className="bg-white/[0.08] border border-white/15 rounded-2xl p-6 sm:p-7">
                    <I className="w-7 h-7 text-emerald-300 mb-4" />
                    <div className="text-[12.5px] sm:text-[13px] uppercase tracking-[0.18em] text-emerald-200/90 font-semibold mb-2.5">{c.label}</div>
                    <div className="text-base sm:text-lg text-white/95 leading-relaxed">{c.text}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Chat scene */}
        {scene?.type === "chat" && phase === "play" && (
          <div className="space-y-4">
            {/* Persistent Intelligence Layer caption strip */}
            {scene.intel && (
              <div className="max-w-[1280px] mx-auto flex items-center gap-3 px-4 py-2.5 bg-white/[0.06] border border-emerald-300/30 rounded-xl text-white" data-testid="intel-strip">
                <IntelIcon className="w-4 h-4 text-emerald-300 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-[10.5px] uppercase tracking-[0.18em] text-emerald-200/90 font-semibold">{scene.intel.label}</div>
                  <div className="text-[13px] text-white/85 leading-snug">{scene.intel.text}</div>
                </div>
                <div className="text-[10.5px] uppercase tracking-[0.18em] text-white/55 hidden sm:block">{String(sceneIdx + 1).padStart(2, "0")} · {scene.title}</div>
              </div>
            )}
            <ChatbotShellMock scrollerRef={scrollerRef} turns={turns} partial={partial} showTyping={showTyping} />
          </div>
        )}

        {/* Dashboard scene */}
        {scene?.type === "dash" && phase === "play" && (
          <DashboardSceneCard scene={scene} chapterLabel={`Chapter ${String(sceneIdx + 1).padStart(2, "0")}`} />
        )}

        {/* Register / done scene */}
        {(scene?.type === "register" || phase === "done") && (
          <div className="text-center max-w-3xl mx-auto px-4" style={{ animation: "scaleIn 0.6s ease-out" }} data-testid="walkthrough-done">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-emerald-400/15 border border-emerald-300/40 rounded-full text-emerald-200 text-xs sm:text-[13px] uppercase tracking-[0.3em] mb-7">
              <div className="w-5 h-5 rounded overflow-hidden bg-[#0b1a3b]">
                <img src={RUDRAS_LOGO} alt="" className="w-full h-full object-contain" />
              </div>
              Rudras-AI
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.1] mb-5">
              You Just Saw Healthcare.
              <br />
              <span className="text-emerald-300">now Imagine Your Business.</span>
            </h2>
            <p className="text-white/85 max-w-2xl mx-auto mb-7 text-base sm:text-lg leading-relaxed">
              Rudras-AI transforms your business knowledge into intelligent AI assistants built for customer engagement, training, and growth.
            </p>
            <div className="flex flex-wrap justify-center gap-2 mb-9">
              {PLATFORM_USE_CASES.map((u) => (
                <span key={u} className="px-3 py-1.5 text-xs sm:text-[13px] uppercase tracking-wider bg-white/[0.08] border border-white/20 rounded-full text-white/80">
                  {u}
                </span>
              ))}
            </div>
            <div className="flex flex-col items-center gap-3">
              <a href={REGISTER_URL} target="_blank" rel="noreferrer noopener" data-testid="register-cta-link">
                <Button size="lg" className="bg-emerald-400 text-emerald-950 hover:bg-emerald-300 font-semibold text-base sm:text-lg px-9 py-6 shadow-[0_10px_40px_-10px_rgba(52,211,153,0.6)]">
                  Build mine now <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </a>
              <div className="text-emerald-200/90 text-sm sm:text-[15px] font-medium tracking-wide">
                Free Trial — No Credit Card Required
              </div>
            </div>
            <div className="flex flex-wrap gap-3 justify-center mt-8">
              {marketingCta && (
                onMarketingCta ? (
                  <Button size="sm" onClick={onMarketingCta} variant="ghost" className="text-white/75 hover:bg-white/10 hover:text-white" data-testid="marketing-cta-contact">
                    Talk to our team <ChevronRight className="w-3.5 h-3.5 ml-1" />
                  </Button>
                ) : (
                  <a href="mailto:hello@rudras-ai.com?subject=Custom%20AI%20Sales%20Assistant%20demo" data-testid="marketing-cta-contact">
                    <Button size="sm" variant="ghost" className="text-white/75 hover:bg-white/10 hover:text-white">
                      Talk to our team <ChevronRight className="w-3.5 h-3.5 ml-1" />
                    </Button>
                  </a>
                )
              )}
              {onSkipToInteractive && (
                <Button size="sm" onClick={onSkipToInteractive} variant="ghost" className="text-white/75 hover:bg-white/10 hover:text-white" data-testid="walkthrough-done-interactive">
                  Explore the dashboard <ChevronRight className="w-3.5 h-3.5 ml-1" />
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={restart} className="text-white/75 hover:bg-white/10 hover:text-white" data-testid="walkthrough-done-restart">
                <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Watch again
              </Button>
            </div>

            {/* Copyright & IP disclaimer — required for public distribution. */}
            <div className="mt-12 pt-7 border-t border-white/10 text-left max-w-3xl mx-auto" data-testid="walkthrough-copyright">
              <div className="text-emerald-200 font-semibold text-sm sm:text-[15px] mb-3">© Rudras-AI. All Rights Reserved.</div>
              <div className="text-white/65 text-[12px] sm:text-[12.5px] leading-relaxed space-y-2.5">
                <p>This cinematic walkthrough, platform design, workflows, architecture, user experience, AI interaction models, branding elements, and related materials are proprietary intellectual property of Rudras-AI.</p>
                <p>Unauthorized copying, reproduction, distribution, reverse engineering, modification, recording, or commercial use of this content, in whole or in part, without prior written permission from Rudras-AI is strictly prohibited.</p>
                <p>The concepts, workflows, AI assistant behaviors, interface designs, and business methodologies demonstrated herein are confidential and intended solely for evaluation, demonstration, and business discussion purposes.</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
